import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Button, 
  Typography, 
  useMediaQuery, 
  useTheme,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  CircularProgress,
  Grid,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import RefreshIcon from '@mui/icons-material/Refresh';
import { Supplier } from '../../types/supplier';
import { MaintenanceRecord } from '../../types/maintenance';
import { FuelConsumption } from '../../types/fuelConsumption';
import { fuelConsumptionService } from '../../services/fuelConsumptionService';
import { getMaintenanceRecords } from '../../services/maintenanceService';
import MaintenanceList from '../maintenance/MaintenanceList';
import TabPanel from '../common/TabPanel';
import { formatDate } from '../../utils/dateUtils';

interface SupplierDetailsProps {
  supplier: Supplier;
  onUpdate: () => void;
  onEditSupplier: (supplier: Supplier) => void;
  onDeleteSupplier: (supplierId: string) => void;
}

const SupplierDetails: React.FC<SupplierDetailsProps> = ({
  supplier,
  onUpdate,
  onEditSupplier,
  onDeleteSupplier
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [activeTab, setActiveTab] = useState(0);
  const [fuelConsumptions, setFuelConsumptions] = useState<FuelConsumption[]>([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState<MaintenanceRecord[]>([]);
  const [isLoadingFuel, setIsLoadingFuel] = useState(false);
  const [isLoadingMaintenance, setIsLoadingMaintenance] = useState(false);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error';
  }>({
    open: false,
    message: '',
    severity: 'success'
  });

  // Load fuel consumptions and maintenance records
  const loadData = async () => {
    setIsLoadingFuel(true);
    setIsLoadingMaintenance(true);
    try {
      // Load fuel consumptions where this supplier is referenced
      const fuelRecords = await fuelConsumptionService.getBySupplier(supplier.id || '');
      setFuelConsumptions(fuelRecords);

      // Load maintenance records where this supplier is referenced
      const maintenanceRecords = await getMaintenanceRecords(supplier.organizationId, {
        serviceProviderId: supplier.id
      });
      setMaintenanceRecords(maintenanceRecords);
    } catch (error) {
      console.error('Error loading data:', error);
      setSnackbar({
        open: true,
        message: t('common.errors.loadFailed'),
        severity: 'error'
      });
    } finally {
      setIsLoadingFuel(false);
      setIsLoadingMaintenance(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [supplier.id]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <Grid container spacing={2}>
      {/* Summary Panel */}
      <Grid item xs={12} md={3}>
        <Paper sx={{ p: 2, height: '100%' }}>
          {/* Basic Info */}
          <Typography variant="h6" gutterBottom>
            {t('supplier.details.sections.basicInfo')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle1">
              {supplier.name}
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              {t(`supplier.type.${supplier.type}`)}
            </Typography>
            <Typography color="text.secondary" gutterBottom>
              {supplier.address}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => onEditSupplier(supplier)}
                fullWidth
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                onClick={() => {
                  if (window.confirm(t('common.confirm'))) {
                    onDeleteSupplier(supplier.id || '');
                  }
                }}
                fullWidth
              >
                {t('common.delete')}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Contact Info */}
          <Typography variant="subtitle2" gutterBottom>
            {t('supplier.details.sections.contact')}
          </Typography>
          <Box sx={{ mb: 2 }}>
            <Typography color="text.secondary">
              {supplier.contactName}
            </Typography>
            <Typography color="text.secondary">
              {supplier.contactPhone}
            </Typography>
            <Typography color="text.secondary">
              {supplier.email}
            </Typography>
          </Box>

          {supplier.websiteUrl && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('supplier.details.fields.website')}
              </Typography>
              <Typography 
                component="a" 
                href={supplier.websiteUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                color="primary"
              >
                {supplier.websiteUrl}
              </Typography>
            </>
          )}

          {supplier.hours && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('supplier.details.sections.hours')}
              </Typography>
              {Object.entries(supplier.hours).map(([day, hours]) => (
                <Typography key={day} color="text.secondary">
                  {t(`common.days.${day}`)}: {hours.open} - {hours.close}
                </Typography>
              ))}
            </>
          )}

          {supplier.services && supplier.services.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            {t('supplier.details.sections.services')}
          </Typography>
              {supplier.services.map((service, index) => (
                <Typography key={index} color="text.secondary">
                  • {service}
                </Typography>
              ))}
            </>
          )}

          {supplier.notes && (
            <>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" gutterBottom>
                {t('supplier.details.fields.notes')}
              </Typography>
              <Typography color="text.secondary">
                {supplier.notes}
              </Typography>
            </>
          )}
        </Paper>
      </Grid>

      {/* Main Content */}
      <Grid item xs={12} md={9}>
        <Box>
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs
              value={activeTab}
              onChange={(_, newValue) => setActiveTab(newValue)}
              aria-label="supplier details tabs"
              variant={isMobile ? "scrollable" : "standard"}
              scrollButtons={isMobile ? "auto" : false}
            >
              <Tab label={t('maintenance.title')} />
              <Tab label={t('fuelReceipt.title')} />
            </Tabs>
          </Box>

          <Box sx={{ mt: 2 }}>
            {/* Maintenance Tab */}
            <TabPanel value={activeTab} index={0}>
              <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
                <Button
                  variant="outlined"
                  onClick={loadData}
                  disabled={isLoadingMaintenance}
                  fullWidth={isMobile}
                  startIcon={<RefreshIcon />}
                >
                  {t('common.refresh')}
                </Button>
              </Box>
              {isLoadingMaintenance ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : (
                <MaintenanceList
                  organizationId={supplier.organizationId}
                  filters={{ serviceProviderId: supplier.id }}
                  onEdit={() => {}}
                />
              )}
            </TabPanel>

            {/* Fuel Tab */}
            <TabPanel value={activeTab} index={1}>
              <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
                <Button
                  variant="outlined"
                  onClick={loadData}
                  disabled={isLoadingFuel}
                  fullWidth={isMobile}
                  startIcon={<RefreshIcon />}
                >
                  {t('common.refresh')}
                </Button>
              </Box>
              {isLoadingFuel ? (
                <Box display="flex" justifyContent="center" my={4}>
                  <CircularProgress />
                </Box>
              ) : fuelConsumptions.length > 0 ? (
                <List>
                  {fuelConsumptions.map((record) => (
                    <ListItem
                      key={record.id}
                      sx={{
                        backgroundColor: 'background.default',
                        borderRadius: 1,
                        mb: 1,
                        '&:hover': {
                          backgroundColor: 'action.hover'
                        }
                      }}
                    >
                      <ListItemText
                        primary={`${record.volume.toFixed(2)} L - ${record.amount.toFixed(2)} €`}
                        secondary={formatDate(record.date)}
                      />
                    </ListItem>
                  ))}
                </List>
              ) : (
                <Typography color="textSecondary">
                  {t('supplier.details.messages.noFuelRecords')}
                </Typography>
              )}
            </TabPanel>
          </Box>
        </Box>
      </Grid>

      {/* Error Snackbar */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      >
        <Alert 
          onClose={() => setSnackbar(prev => ({ ...prev, open: false }))} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default SupplierDetails;
